import { type FieldsProps } from './WorkshopTypeFormState';

export {
  buildPayload,
  dollarsToCents,
  durationToMinutes,
  persistWorkshopType,
  seedDuration,
  seedFormFromType,
  seedPrice,
  useWorkshopTypeForm,
  workshopTypeErrorMessage,
} from './WorkshopTypeFormState';
export type {
  FieldsProps,
  FormSetters,
  WorkshopTypeFormApi,
  WorkshopTypeFormState,
} from './WorkshopTypeFormState';

export function WorkshopTypeFields({
  form,
  treeSpeciesList,
  setName,
  setDisplayName,
  setTreeSpeciesId,
  setDescription,
  setDefaultPrice,
  setDefaultCapacity,
  setDurationValue,
  setDurationUnit,
  setCoveredByTreeGuarantee,
  setIsActive,
}: FieldsProps) {
  return (
    <>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1">
            Name *
          </label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setName(e.target.value)}
            required
            className="w-full px-3 py-2 rounded-lg border border-stone-300 dark:border-stone-600 bg-white dark:bg-[#242d28] text-bark-900 dark:text-stone-100 text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1">
            Display Name
          </label>
          <input
            type="text"
            value={form.displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Public-facing name"
            className="w-full px-3 py-2 rounded-lg border border-stone-300 dark:border-stone-600 bg-white dark:bg-[#242d28] text-bark-900 dark:text-stone-100 text-sm"
          />
        </div>
      </div>

      <WorkshopTypeDetailFields
        form={form}
        treeSpeciesList={treeSpeciesList}
        setTreeSpeciesId={setTreeSpeciesId}
        setDescription={setDescription}
        setDefaultPrice={setDefaultPrice}
        setDefaultCapacity={setDefaultCapacity}
        setDurationValue={setDurationValue}
        setDurationUnit={setDurationUnit}
        setCoveredByTreeGuarantee={setCoveredByTreeGuarantee}
        setIsActive={setIsActive}
      />
    </>
  );
}

/** Duration / Price / Capacity row of the workshop-type form. */
function WorkshopTypeNumericFields({
  form,
  setDefaultPrice,
  setDefaultCapacity,
  setDurationValue,
  setDurationUnit,
}: Pick<
  FieldsProps,
  'form' | 'setDefaultPrice' | 'setDefaultCapacity' | 'setDurationValue' | 'setDurationUnit'
>) {
  return (
    <div className="flex flex-wrap gap-4">
      <div>
        <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1">
          Duration
        </label>
        <div className="flex gap-1.5">
          <input
            type="number"
            min="0"
            step={form.durationUnit === 'hours' ? '0.5' : '1'}
            value={form.durationValue}
            onChange={(e) => setDurationValue(e.target.value)}
            className="w-20 px-2 py-1.5 rounded-lg border border-stone-300 dark:border-stone-600 bg-white dark:bg-[#242d28] text-bark-900 dark:text-stone-100 text-sm"
          />
          <select
            value={form.durationUnit}
            onChange={(e) => setDurationUnit(e.target.value as 'hours' | 'minutes')}
            className="px-2 py-1.5 rounded-lg border border-stone-300 dark:border-stone-600 bg-white dark:bg-[#242d28] text-bark-900 dark:text-stone-100 text-sm"
          >
            <option value="hours">hrs</option>
            <option value="minutes">min</option>
          </select>
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1">
          Price
        </label>
        <div className="relative">
          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-stone-400">
            $
          </span>
          <input
            type="number"
            min="0"
            step="0.01"
            value={form.defaultPrice}
            onChange={(e) => setDefaultPrice(e.target.value)}
            placeholder="0.00"
            className="w-28 pl-6 pr-2 py-1.5 rounded-lg border border-stone-300 dark:border-stone-600 bg-white dark:bg-[#242d28] text-bark-900 dark:text-stone-100 text-sm"
          />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1">
          Capacity
        </label>
        <input
          type="number"
          min="1"
          step="1"
          value={form.defaultCapacity}
          onChange={(e) => setDefaultCapacity(e.target.value)}
          placeholder="Seats"
          className="w-24 px-2 py-1.5 rounded-lg border border-stone-300 dark:border-stone-600 bg-white dark:bg-[#242d28] text-bark-900 dark:text-stone-100 text-sm"
        />
      </div>
    </div>
  );
}

function WorkshopTypeDetailFields({
  form,
  treeSpeciesList,
  setTreeSpeciesId,
  setDescription,
  setDefaultPrice,
  setDefaultCapacity,
  setDurationValue,
  setDurationUnit,
  setCoveredByTreeGuarantee,
  setIsActive,
}: Omit<FieldsProps, 'setName' | 'setDisplayName'>) {
  return (
    <>
      <WorkshopTypeNumericFields
        form={form}
        setDefaultPrice={setDefaultPrice}
        setDefaultCapacity={setDefaultCapacity}
        setDurationValue={setDurationValue}
        setDurationUnit={setDurationUnit}
      />

      <div>
        <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1">
          Tree Species
        </label>
        <select
          value={form.treeSpeciesId}
          onChange={(e) => setTreeSpeciesId(e.target.value)}
          className="w-full px-3 py-2 rounded-lg border border-stone-300 dark:border-stone-600 bg-white dark:bg-[#242d28] text-bark-900 dark:text-stone-100 text-sm"
        >
          <option value="">— None —</option>
          {treeSpeciesList.map((s) => (
            <option key={s.id} value={s.id}>
              {s.commonName}
            </option>
          ))}
        </select>
      </div>

      <WorkshopTypeFlagFields
        form={form}
        setDescription={setDescription}
        setCoveredByTreeGuarantee={setCoveredByTreeGuarantee}
        setIsActive={setIsActive}
      />
    </>
  );
}

function WorkshopTypeFlagFields({
  form,
  setDescription,
  setCoveredByTreeGuarantee,
  setIsActive,
}: Pick<FieldsProps, 'form' | 'setDescription' | 'setCoveredByTreeGuarantee' | 'setIsActive'>) {
  return (
    <>
      <div className="flex flex-wrap gap-x-6 gap-y-2">
        <label className="flex items-center gap-2.5 cursor-pointer">
          <input
            type="checkbox"
            checked={form.coveredByTreeGuarantee}
            onChange={(e) => setCoveredByTreeGuarantee(e.target.checked)}
            className="w-4 h-4 rounded accent-moss-600"
          />
          <span className="text-sm font-medium text-stone-700 dark:text-stone-300">
            Covered by tree guarantee
          </span>
        </label>
        <label className="flex items-center gap-2.5 cursor-pointer">
          <input
            type="checkbox"
            checked={form.isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            className="w-4 h-4 rounded accent-moss-600"
          />
          <span className="text-sm font-medium text-stone-700 dark:text-stone-300">Active</span>
        </label>
      </div>

      <div>
        <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1">
          Description
        </label>
        <textarea
          rows={2}
          value={form.description}
          onChange={(e) => setDescription(e.target.value)}
          className="w-full px-3 py-2 rounded-lg border border-stone-300 dark:border-stone-600 bg-white dark:bg-[#242d28] text-bark-900 dark:text-stone-100 text-sm"
        />
      </div>
    </>
  );
}
